/**
 * 活动道具玩法格（2026-10-05 leo：活动道具像领养点一样进副玩法组）。
 *
 * 活动道具原来只是掉落项（spin 行上的 `act_item`），没有玩法格，客户端就近找不到它，进不了组。
 * 现在给它一个 EVENT 玩法格 `act_item`：在副玩法页勾组，组行抽中后客户端可以就近挑它，
 * 领取（POST /v1/game/subgame）时与掷骰踩中活动道具行同一段结算（main-roll.ts 的 act_item 分支）：
 * 有生效中的活动发当期道具（基础值 × 倍数），空窗期 / 满档按回退发金币或骰子。
 *
 * 基础值与回退配在这一格的 params 上（掉落项上那三项在这里没有行可挂）。
 * 本文件不依赖别的模块：管端写入口、发布闸、运行时三处共用同一份判定。
 */
export const ACT_ITEM_TILE = "act_item";

export const ACT_ITEM_TILE_FALLBACK_KINDS = ["coin", "dice"] as const;

export interface ActItemTileParams {
  /** 基础值：有活动时实发 = 基础值 × 倍数 */
  value: number;
  /** 空窗期（或满档且活动选了回退）改发什么 */
  fallbackKind: (typeof ACT_ITEM_TILE_FALLBACK_KINDS)[number];
  /** 回退数量：金币按 B 值计（× B × 倍数），骰子按个数（不乘倍数） */
  fallbackValue: number;
}

/** 新建这一格时预填的参数，与 spin 表勾「活动道具」时的预填同值（BoardPage） */
export const ACT_ITEM_TILE_DEFAULTS: ActItemTileParams = { value: 10, fallbackKind: "coin", fallbackValue: 1 };

/** 参数哪里不对；null = 合法。上下界与掉落项上的 act_item 同口径（admin/config-content.ts putSpinCells） */
export function actItemTileFault(params: unknown): string | null {
  const p = (params && typeof params === "object" ? params : {}) as Record<string, unknown>;
  const v = p.value, fk = p.fallbackKind, fv = p.fallbackValue;
  if (!Number.isInteger(v) || (v as number) < 1 || (v as number) > 2_000_000) return "基础值必须是 1 ~ 2000000 的整数（实发 = 基础值 × 倍数）";
  if (!(ACT_ITEM_TILE_FALLBACK_KINDS as readonly unknown[]).includes(fk)) return `空窗期回退必须是 ${ACT_ITEM_TILE_FALLBACK_KINDS.join(" | ")}`;
  if (!Number.isInteger(fv) || (fv as number) < 1 || (fv as number) > 2_000_000) return "空窗期回退数量必须是 1 ~ 2000000 的整数";
  return null;
}

/** 解析成运行时用的参数；不合法返回 null（写入口与发布闸已拦，走到这里是配置被绕过写坏） */
export function actItemTileParams(params: unknown): ActItemTileParams | null {
  if (actItemTileFault(params)) return null;
  const p = params as Record<string, unknown>;
  return { value: p.value as number, fallbackKind: p.fallbackKind as ActItemTileParams["fallbackKind"], fallbackValue: p.fallbackValue as number };
}
